import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import {
    exportDetailedDataDownload,
    exportDetailedDataInternalDownload,
    getDetailedData,
    getDetailedDataInternal,
} from '../../../api/external-reports';
import ReportSection from '../ReportSection';
import { detailedViewFilters } from '../ReportsHelper';
import Loader, { Types } from '../../gdk/Loader';
import ExpandableTable from './ExpandableTable';
import ReportHeaderSection from '../ReportHeaderSection';
import NoData from '../NoData';

function getActiveFilterKeys(filters, includeDateFilters = true) {
    return Object.keys(filters).filter(key => {
        if (key === 'startDate' || key === 'endDate') {
            return includeDateFilters && !!filters[key];
        }
        return Array.isArray(filters[key]) && filters[key].length > 0;
    });
}

function getNonDateFilters(filters) {
    const { startDate, endDate, ...nonDateFilters } = filters ?? {};
    return nonDateFilters;
}

function applyNonDateFiltersToDomain(domain, filters) {
    if (!domain?.TableData) {
        return null;
    }

    const nonDateFilters = getNonDateFilters(filters);
    if (getActiveFilterKeys(nonDateFilters, false).length === 0) {
        return domain;
    }

    const filteredData = { TableData: domain.TableData.filter(row => filterRow(row, nonDateFilters)) };
    return filteredData.TableData.length === 0 ? null : filteredData;
}

// Top-level for testing
export function filterRow(row, filters) {
    return getActiveFilterKeys(filters).every(key => {
        switch (key) {
            case 'startDate':
                return new Date(row.ReportingDate) >= new Date(filters[key]);
            case 'endDate':
                return new Date(row.ReportingDate) <= new Date(filters[key]);
            case 'AgentSourceOfBusiness':
                return filters[key].map(x => x.split("-")[0]).includes(row.AgentSourceOfBusiness);
            default:
                return filters[key].includes(row[detailedViewFilters.find(filter => filter.value === key)?.value]);
        }
    });
}

export default function ReportDetailedPage({
    user,
    substituteAgentInfo,
    data,
    setData = () => { },
    dataLastUpdatedDate,
    dataStartDate,
    filterOptions,
    selectedDateRange,
    setSelectedDateRange = () => { },
    appliedFilters,
    setAppliedFilters = () => { }
}) {
    const [unfilteredData, setUnfilteredData] = useState(data);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [expandedRows, setExpandedRows] = useState([]);
    const appliedFiltersRef = useRef(appliedFilters);
    const loadRequestIdRef = useRef(0);

    useEffect(() => {
        appliedFiltersRef.current = appliedFilters;
    }, [appliedFilters]);

    useEffect(() => {
        if (!data) {
            loadData()
            setSelectedDateRange('All In');
        }
    }, []);

    function applyClientSideFilters(filters) {
        appliedFiltersRef.current = filters;
        setAppliedFilters(filters);
        if (unfilteredData) {
            setData(applyNonDateFiltersToDomain(unfilteredData, filters));
        }
    }

    async function loadData(selectedFilters = {}) {
        const requestId = ++loadRequestIdRef.current;
        appliedFiltersRef.current = selectedFilters;
        setLoading(true);
        const handleSuccess = (response) => {
            if (requestId !== loadRequestIdRef.current) {
                return;
            }

            const currentFilters = appliedFiltersRef.current;

            if (response?.Status === 3) {
                const domain = response?.Domain;
                setUnfilteredData(domain);
                setData(applyNonDateFiltersToDomain(domain, currentFilters));
            } else {
                setUnfilteredData(null);
                setData(null);
            }

            setLoading(false);
        }

        const request = {
            startDate: selectedFilters.startDate,
            endDate: selectedFilters.endDate,
        };

        if (substituteAgentInfo?.id) {
            const substituteAgentRequest = {
                substituteAgentId: substituteAgentInfo.id,
                ...request,
            };
            getDetailedDataInternal(substituteAgentRequest, response => handleSuccess(response))
        } else {
            getDetailedData(request, response => handleSuccess(response))
        }
    }

    async function handleExport() {
        const currentFilters = appliedFiltersRef.current ?? {};
        const request = {
            startDate: currentFilters.startDate,
            endDate: currentFilters.endDate,
        };

        setExporting(true);
        try {
            if (substituteAgentInfo?.id) {
                await exportDetailedDataInternalDownload({
                    substituteAgentId: substituteAgentInfo.id,
                    ...request,
                });
            } else {
                await exportDetailedDataDownload(request);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setExporting(false);
        }
    }

    function calculateMetricCards(data) {
        if (data?.TableData) {
            const metrics = data.TableData.reduce((acc, row) => {
                acc.quotes += row.All_quotes ?? 0;
                acc.newBusiness += row.All_new_business ?? 0;
                acc.boundPremium += row.All_bound_premium ?? 0;
                acc.writtenPremium += row.WrittenPremium ?? 0;
                acc.attritionThirtyDay += row?.AttritionThirtyDay ?? 0;
                acc.attritionThirtyDayVolume += row?.AttritionThirtyDayVolume ?? 0;
                acc.earnedPremium += row?.EarnedPremium ?? 0;
                acc.netLossPay += (row?.NetLossPayments ?? 0) + (row?.NetExpensePayments ?? 0) + (row?.ChangeCaseReserve ?? 0);
                return acc;
            }, { quotes: 0, newBusiness: 0, boundPremium: 0, writtenPremium: 0, attritionThirtyDay: 0, attritionThirtyDayVolume: 0, earnedPremium: 0, netLossPay: 0 });

            return {
                "Quotes": metrics.quotes,
                "NewBusiness": metrics.newBusiness,
                "Conversion": metrics.quotes > 0 ? (metrics.newBusiness / metrics.quotes) : null,
                "BoundPremium": metrics.boundPremium,
                "WrittenPremium": metrics.writtenPremium,
                "AttritionThirtyDay": metrics.attritionThirtyDayVolume > 0 ? (metrics.attritionThirtyDay / metrics.attritionThirtyDayVolume) : null,
                "LossRatio": metrics.earnedPremium > 0 ? (metrics.netLossPay / metrics.earnedPremium) : null
            }
        }
    }

    const expandableTableData = data?.TableData.filter(x => x.All_quotes > 0 || x.All_new_business > 0 || x.All_bound_premium > 0);
    const tableContent = expandableTableData?.length > 0
        ? <ExpandableTable data={expandableTableData} expandedRows={expandedRows} setExpandedRows={setExpandedRows} />
        : <NoData />;

    return <>
        <ReportHeaderSection
            user={user}
            substituteUser={substituteAgentInfo}
            title={"Agency Scorecard"}
            onExport={handleExport}
            exportDisabled={exporting || (loading && !data)}
        />
        <ReportSection
            cards={data && !loading ? calculateMetricCards(data) : null}
            substituteAgentInfo={substituteAgentInfo}
            dateFilters
            additionalFilters={detailedViewFilters}
            applyFilters={applyClientSideFilters}
            applyDateFilters={loadData}
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
            selectedDateRange={selectedDateRange}
            setSelectedDateRange={setSelectedDateRange}
            filterOptions={filterOptions}
            dataStartDate={dataStartDate}
            dataLastUpdatedDate={dataLastUpdatedDate}
        >
            {loading ? <Loader type={Types.Inline} /> : tableContent}
        </ReportSection>
    </>
}

ReportDetailedPage.propTypes = {
    user: PropTypes.object.isRequired,
    substituteAgentInfo: PropTypes.object,
    data: PropTypes.object,
    setData: PropTypes.func,
    dataLastUpdatedDate: PropTypes.instanceOf(Date),
    dataStartDate: PropTypes.instanceOf(Date),
    filterOptions: PropTypes.object,
    selectedDateRange: PropTypes.string,
    setSelectedDateRange: PropTypes.func,
    appliedFilters: PropTypes.object,
    setAppliedFilters: PropTypes.func
};